/* ==========================================================================
   Scandi TR — satın alma tarafı
   <sc-offer>  : paket + tat seçici, satın alma butonları, mobil sabit çubuk
   <sc-cart>   : sepet çekmecesi (hedef çubuğu, paket satırı, tat düzenleme,
                 çapraz satış, otomatik hediye ürünü)
   Ödeme, indirim ve kargo hesapları Shopify'da kalır; bu dosya yalnızca
   sepete doğru ürünleri doğru miktarda ekler ve sepeti gösterir.
   ========================================================================== */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const rootUrl = (path) => `${root.replace(/\/$/, '')}${path}`;

/* ---------- Yardımcılar ---------- */

// Liquid'deki money_without_trailing_zeros gibi: kuruş 00 ise ondalığı yazmaz
export function formatMoney(cents, format = '{{amount}} TL') {
  const value = Number(cents || 0) / 100;
  const fmt = (n, decimals, thousands, decimal) => {
    const [int, frac] = n.toFixed(decimals).split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac && !/^0+$/.test(frac) ? `${grouped}${decimal}${frac}` : grouped;
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return fmt(value, 0, ',', '.');
      case 'amount_with_comma_separator':
        return fmt(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return fmt(value, 0, '.', ',');
      case 'amount_with_space_separator':
        return fmt(value, 2, ' ', ',');
      case 'amount_no_decimals_with_space_separator':
        return fmt(value, 0, ' ', ',');
      case 'amount_with_apostrophe_separator':
        return fmt(value, 2, "'", '.');
      default:
        return fmt(value, 2, ',', '.');
    }
  });
}

const esc = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const fill = (template, values) =>
  String(template || '').replace(/\[(\w+)\]/g, (whole, key) => (key in values ? values[key] : whole));

const sized = (src, width) => {
  if (!src) return '';
  return `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
};

async function cartRequest(path, body) {
  const response = await fetch(rootUrl(path), {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = data.description || data.message || `Sepet hatası (${response.status})`;
    throw Object.assign(new Error(message), { status: response.status, data });
  }
  return data;
}

const getCart = () => cartRequest('/cart.js');

function announce(cart, source) {
  document.dispatchEvent(new CustomEvent('sc:cart', { detail: { cart, source } }));
  // Horizon gibi temaların header sepet sayacını güncellemesi için
  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: { resource: cart, sourceId: source, data: { itemCount: cart.item_count, source } },
    })
  );
}

/* ==========================================================================
   <sc-offer> — paket ve tat seçici
   ========================================================================== */

class ScOffer extends HTMLElement {
  connectedCallback() {
    if (this.cfg) return;
    const blob = this.querySelector('[data-sc-offer-config]');
    if (!blob) return;
    this.cfg = JSON.parse(blob.textContent);
    this.cfg.flavours.forEach((f) => (f.id = String(f.id)));
    this.money = (cents) => formatMoney(cents, this.dataset.moneyFormat);

    const preferred =
      this.cfg.flavours.find((f) => f.id === String(this.cfg.defaultVariant) && f.available) ||
      this.cfg.flavours.find((f) => f.available) ||
      this.cfg.flavours[0];
    this.defaultId = preferred ? preferred.id : null;
    this.tierId = this.cfg.defaultTier || (this.cfg.tiers[0] && this.cfg.tiers[0].id);
    this.counts = this.startingMix();
    this.busy = false;

    this.section = this.closest('[data-sc-section]') || document;
    this.sticky = this.section.querySelector('[data-sc-sticky]');

    this.addEventListener('click', this.onClick);
    this.sticky?.addEventListener('click', this.onStickyClick);
    this.watchBuyZone();
    this.paint();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.sticky?.removeEventListener('click', this.onStickyClick);
    this.io?.disconnect();
  }

  tier() {
    return this.cfg.tiers.find((t) => t.id === this.tierId) || this.cfg.tiers[0];
  }

  boxes(tier = this.tier()) {
    return tier.paid + tier.free;
  }

  chosen() {
    return Object.values(this.counts).reduce((n, q) => n + q, 0);
  }

  startingMix() {
    const counts = {};
    this.cfg.flavours.forEach((f) => (counts[f.id] = 0));
    if (this.defaultId) counts[this.defaultId] = this.boxes();
    return counts;
  }

  // Liquid tarafındaki hesapla birebir aynı (tam sayı kuruş, aşağı yuvarlama)
  pricing(tier = this.tier()) {
    // Pakete sabit fiyat girildiyse o kullanılır, yoksa kutu fiyatı × ücretli kutu − paket indirimi
    const once =
      tier.fixed != null ? tier.fixed : Math.floor((this.cfg.price * tier.paid * (100 - tier.discount)) / 100);
    const sub = this.cfg.subscription.enabled
      ? Math.floor((once * (100 - this.cfg.subscription.percent)) / 100)
      : null;
    const shown = sub ?? once;
    // Kampanya oranı varsa tüm paketlerde üstü çizili fiyat = fiyat / (1 - oran)
    const campaign = Number(this.cfg.campaign) || 0;
    const compare = campaign > 0 ? Math.floor((shown * 100) / (100 - campaign)) : this.cfg.compare * this.boxes(tier);
    const off = campaign > 0 ? campaign : compare > 0 ? 100 - Math.floor((shown * 100) / compare) : 0;
    return { once, sub, compare, shown, off };
  }

  onClick = (event) => {
    const card = event.target.closest('[data-sc-tier]');
    if (card) return this.setTier(card.dataset.scTier);
    const add = event.target.closest('[data-sc-add]');
    if (add) return this.add(add.dataset.scAdd);
    const minus = event.target.closest('[data-sc-minus]');
    if (minus) return this.remove(minus.dataset.scMinus);
    const buy = event.target.closest('[data-sc-buy]');
    if (buy) return this.submit(buy.dataset.scBuy === 'subscribe');
    return undefined;
  };

  onStickyClick = (event) => {
    if (event.target.closest('[data-sc-sticky-buy]')) this.submit(this.cfg.subscription.enabled);
  };

  setTier(id) {
    if (this.tierId === id) return;
    this.tierId = id;
    this.counts = this.startingMix();
    this.paint();
  }

  // Paket doluyken başka bir tat artırılırsa en çok seçilen tattan bir kutu alınır
  add(id) {
    const flavour = this.cfg.flavours.find((f) => f.id === id);
    if (!flavour || !flavour.available) return;
    if (this.chosen() < this.boxes()) {
      this.counts[id] = (this.counts[id] || 0) + 1;
    } else {
      const donor = this.cfg.flavours
        .filter((f) => f.id !== id && this.counts[f.id] > 0)
        .sort((a, b) => {
          const diff = this.counts[b.id] - this.counts[a.id];
          if (diff !== 0) return diff;
          if (a.id === this.defaultId) return -1;
          if (b.id === this.defaultId) return 1;
          return 0;
        })[0];
      if (!donor) return;
      this.counts[donor.id] -= 1;
      this.counts[id] = (this.counts[id] || 0) + 1;
    }
    this.paint();
  }

  remove(id) {
    if (!this.counts[id]) return;
    this.counts[id] -= 1;
    this.paint();
  }

  values() {
    const tier = this.tier();
    const p = this.pricing(tier);
    return {
      tier,
      p,
      base: {
        kutu: this.boxes(tier),
        ucretli: tier.paid,
        bedava: tier.free,
        hafta: this.cfg.subscription.weeks,
        yuzde: this.cfg.subscription.percent,
        indirim: p.off,
        eski_fiyat: this.money(p.compare),
      },
    };
  }

  paint() {
    const s = this.cfg.strings;
    const { tier, p, base } = this.values();
    const boxes = this.boxes(tier);
    const filled = this.chosen();

    this.querySelectorAll('[data-sc-tier]').forEach((card) => {
      const on = card.dataset.scTier === tier.id;
      card.classList.toggle('is-on', on);
      card.setAttribute('aria-checked', String(on));
    });

    const slots = this.querySelector('[data-sc-slots]');
    if (slots) {
      slots.innerHTML = Array.from(
        { length: boxes },
        (_, i) => `<span class="sc-slot${i < filled ? ' is-filled' : ''}"></span>`
      ).join('');
    }

    const note = this.querySelector('[data-sc-slotnote]');
    if (note) {
      const tag = tier.free > 0 ? `<span class="sc-slotnote__tag">${esc(fill(s.freeTag, base))}</span>` : '';
      note.innerHTML = `<span>${esc(fill(s.slotNote, base))}</span>${tag}`;
    }

    this.querySelectorAll('[data-sc-tile]').forEach((tile) => {
      const id = tile.dataset.scTile;
      const count = this.counts[id] || 0;
      tile.classList.toggle('is-on', count > 0);
      const qty = tile.querySelector('[data-sc-qty]');
      if (qty) qty.textContent = String(count);
      const minus = tile.querySelector('[data-sc-minus]');
      if (minus) minus.disabled = count === 0;
    });

    const shortfall = this.querySelector('[data-sc-shortfall]');
    if (shortfall) {
      const left = boxes - filled;
      const flavour = this.cfg.flavours.find((f) => f.id === this.defaultId);
      shortfall.hidden = left <= 0;
      if (left > 0) shortfall.textContent = fill(left === 1 ? s.shortfallOne : s.shortfall, { n: left, tat: flavour ? flavour.name : '' });
    }

    const subValues = { ...base, fiyat: this.money(p.sub ?? p.once), tasarruf: this.money(Math.max(0, p.compare - (p.sub ?? p.once))) };
    const onceValues = { ...base, fiyat: this.money(p.once), tasarruf: this.money(Math.max(0, p.compare - p.once)) };
    this.setText('[data-sc-sub-main]', fill(s.subMain, subValues));
    this.setText('[data-sc-sub-sub]', fill(s.subSub, subValues));
    this.setText('[data-sc-once-main]', fill(s.onceMain, onceValues));
    this.setText('[data-sc-once-sub]', fill(s.onceSub, onceValues));

    if (this.sticky) {
      const shownValues = this.cfg.subscription.enabled ? subValues : onceValues;
      const sub = this.sticky.querySelector('[data-sc-sticky-sub]');
      const price = this.sticky.querySelector('[data-sc-sticky-price]');
      const was = this.sticky.querySelector('[data-sc-sticky-was]');
      if (sub) sub.textContent = fill(s.stickySub, shownValues);
      if (price) price.textContent = this.money(p.shown);
      if (was) {
        was.textContent = this.money(p.compare);
        was.hidden = p.compare <= p.shown;
      }
    }
  }

  setText(selector, text) {
    const el = this.querySelector(selector);
    if (el) el.textContent = text;
  }

  // Satın alma alanı ekrandan yukarı çıkınca alttaki sabit çubuğu göster
  watchBuyZone() {
    const zone = this.querySelector('[data-sc-buy-zone]');
    if (!this.sticky || !zone || !('IntersectionObserver' in window)) return;
    this.io = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.sticky.classList.toggle('is-visible', show);
      this.sticky.inert = !show;
      this.sticky.setAttribute('aria-hidden', String(!show));
    });
    this.io.observe(zone);
  }

  setBusy(busy) {
    this.querySelectorAll('[data-sc-buy]').forEach((b) => b.setAttribute('aria-busy', String(busy)));
    this.sticky?.querySelector('[data-sc-sticky-buy]')?.setAttribute('aria-busy', String(busy));
  }

  showError(message) {
    const box = this.querySelector('[data-sc-error]');
    if (!box) return;
    box.textContent = message || '';
    box.hidden = !message;
  }

  async submit(subscribe) {
    if (this.busy) return;
    const tier = this.tier();
    const counts = { ...this.counts };
    const short = this.boxes(tier) - this.chosen();
    if (short > 0 && this.defaultId) counts[this.defaultId] = (counts[this.defaultId] || 0) + short;

    const sub = this.cfg.subscription;
    const plan = subscribe && sub.enabled && sub.planId ? { selling_plan: Number(sub.planId) } : {};
    const properties = { _sc_paket: `${tier.paid}+${tier.free}` };
    const items = this.cfg.flavours
      .filter((f) => counts[f.id] > 0)
      .map((f) => ({ id: Number(f.id), quantity: counts[f.id], properties, ...plan }));
    if (tier.giftPaste && this.cfg.paste) {
      items.push({ id: Number(this.cfg.paste.variantId), quantity: 1, properties: { _sc_hediye: '1' } });
    }
    if (!items.length) return;

    this.busy = true;
    this.setBusy(true);
    this.showError('');
    // Kapıda ödeme akışlarında sepet çekmecesi açılmaz: 'easysell' EasySell formunu açar,
    // 'odeme' müşteriyi doğrudan Shopify ödeme sayfasına götürür
    const flow = this.cfg.flow || 'sepet';
    const direct = flow === 'easysell' || flow === 'odeme';
    const hasDrawer = !direct && Boolean(document.querySelector('sc-cart'));
    if (hasDrawer) document.dispatchEvent(new CustomEvent('sc:cart:open', { detail: { pending: true } }));
    let leaving = false;

    try {
      // Sepetteki eski sakız paketini ve hediyeyi çıkar, diğer ürünlere dokunma
      const cart = await getCart();
      const updates = {};
      cart.items.forEach((item) => {
        if (item.product_id === this.cfg.productId || (item.properties && item.properties._sc_hediye)) {
          updates[item.key] = 0;
        }
      });
      if (Object.keys(updates).length) await cartRequest('/cart/update.js', { updates });
      await cartRequest('/cart/add.js', { items });
      const fresh = await getCart();
      announce(fresh, 'sc-offer');
      // EasySell, sepetteki ödeme butonunun önüne kendi butonunu ekler; o buton formu sepet içeriğiyle açar
      const codButton = flow === 'easysell' ? document.querySelector('.es-popup-button-drawer') : null;
      if (codButton) {
        codButton.click();
      } else if (direct || !hasDrawer) {
        leaving = true;
        window.location.href = rootUrl(direct ? '/checkout' : '/cart');
      }
    } catch (error) {
      console.error('[Scandi TR] sepete ekleme başarısız', error);
      if (hasDrawer) document.dispatchEvent(new CustomEvent('sc:cart:cancel'));
      this.showError(error.message || this.cfg.strings.error);
    } finally {
      // Sayfadan ayrılırken buton meşgul kalır, böylece ikinci tıklama ikinci kez eklemez
      if (!leaving) {
        this.busy = false;
        this.setBusy(false);
      }
    }
  }
}

if (!customElements.get('sc-offer')) customElements.define('sc-offer', ScOffer);

/* ==========================================================================
   <sc-cart> — sepet çekmecesi
   ========================================================================== */

class ScCart extends HTMLElement {
  connectedCallback() {
    if (this.cfg) return;
    const blob = this.querySelector('[data-sc-cart-config]');
    this.dialog = this.querySelector('dialog');
    if (!blob || !this.dialog) return;
    this.cfg = JSON.parse(blob.textContent);
    this.cfg.flavours = (this.cfg.flavours || []).map((f) => ({ ...f, id: String(f.id) }));
    this.money = (cents) => formatMoney(cents, this.dataset.moneyFormat);
    this.busy = false;
    this.cart = null;

    document.addEventListener('sc:cart', this.onCart);
    document.addEventListener('sc:cart:open', this.onOpen);
    document.addEventListener('sc:cart:cancel', this.onCancel);
    this.addEventListener('click', this.onClick);
    this.dialog.addEventListener('cancel', this.onEscape);
    if (this.cfg.hijack) document.addEventListener('click', this.onDocumentClick, true);
  }

  disconnectedCallback() {
    document.removeEventListener('sc:cart', this.onCart);
    document.removeEventListener('sc:cart:open', this.onOpen);
    document.removeEventListener('sc:cart:cancel', this.onCancel);
    document.removeEventListener('click', this.onDocumentClick, true);
    this.removeEventListener('click', this.onClick);
    this.dialog?.removeEventListener('cancel', this.onEscape);
  }

  $(selector) {
    return this.querySelector(selector);
  }

  icon(name) {
    const tpl = this.querySelector(`template[data-sc-icon="${name}"]`);
    return tpl ? tpl.innerHTML : '';
  }

  /* ---------- Açma / kapama ---------- */

  async open(pending = false) {
    this.dialog.classList.remove('is-closing');
    if (!this.dialog.open) this.dialog.showModal();
    this.classList.toggle('is-loading', pending);
    if (pending) return;
    try {
      this.render(await getCart());
    } catch (error) {
      console.error('[Scandi TR] sepet okunamadı', error);
      this.classList.remove('is-loading');
    }
  }

  close() {
    if (!this.dialog.open) return;
    this.closeSheet();
    if (reduceMotion) {
      this.dialog.close();
      return;
    }
    this.dialog.classList.add('is-closing');
    setTimeout(() => {
      this.dialog.close();
      this.dialog.classList.remove('is-closing');
    }, 220);
  }

  onOpen = (event) => this.open(Boolean(event.detail && event.detail.pending));

  onCancel = () => {
    this.classList.remove('is-loading');
    this.close();
  };

  onCart = (event) => this.render(event.detail.cart);

  onEscape = (event) => {
    event.preventDefault();
    if (this.$('[data-sc-sheet]')?.classList.contains('is-open')) this.closeSheet();
    else this.close();
  };

  // Header'daki sepet bağlantısı (/cart) tıklanınca sayfaya gitmek yerine çekmeceyi aç
  onDocumentClick = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link || this.contains(link)) return;
    let path;
    try {
      path = new URL(link.href, window.location.href).pathname;
    } catch (error) {
      return;
    }
    if (!/\/cart\/?$/.test(path)) return;
    event.preventDefault();
    this.open();
  };

  /* ---------- Sepet değişiklikleri ---------- */

  async mutate(task) {
    if (this.busy) return;
    this.busy = true;
    this.classList.add('is-busy');
    this.showError('');
    try {
      await task();
      await this.syncGift();
      announce(await getCart(), 'sc-cart');
    } catch (error) {
      console.error('[Scandi TR] sepet güncellenemedi', error);
      this.showError(error.message);
    } finally {
      this.busy = false;
      this.classList.remove('is-busy');
    }
  }

  // Hediye ürün (ör. diş macunu) yalnızca kutu sayısı eşiği geçince sepette kalır
  async syncGift() {
    const gift = this.cfg.gift;
    if (!gift) return;
    const cart = await getCart();
    const boxes = cart.items.filter((i) => i.product_id === this.cfg.gumProductId).reduce((n, i) => n + i.quantity, 0);
    const lines = cart.items.filter((i) => i.properties && i.properties._sc_hediye);
    const want = boxes >= gift.at;
    if (want && !lines.length) {
      await cartRequest('/cart/add.js', { items: [{ id: Number(gift.variantId), quantity: 1, properties: { _sc_hediye: '1' } }] });
    } else if (!want && lines.length) {
      const updates = {};
      lines.forEach((l) => (updates[l.key] = 0));
      await cartRequest('/cart/update.js', { updates });
    } else if (want && (lines.length > 1 || lines[0].quantity !== 1)) {
      const updates = {};
      lines.forEach((l, i) => (updates[l.key] = i === 0 ? 1 : 0));
      await cartRequest('/cart/update.js', { updates });
    }
  }

  gumLines() {
    return (this.cart ? this.cart.items : []).filter((i) => i.product_id === this.cfg.gumProductId);
  }

  addBox() {
    return this.mutate(async () => {
      const lines = this.gumLines();
      const target =
        lines.find((l) => String(l.variant_id) === String(this.cfg.defaultVariant)) ||
        [...lines].sort((a, b) => b.quantity - a.quantity)[0];
      if (target) await cartRequest('/cart/change.js', { id: target.key, quantity: target.quantity + 1 });
      else await cartRequest('/cart/add.js', { items: [{ id: Number(this.cfg.defaultVariant), quantity: 1 }] });
    });
  }

  removeBox() {
    return this.mutate(async () => {
      const target = [...this.gumLines()].sort((a, b) => b.quantity - a.quantity)[0];
      if (target) await cartRequest('/cart/change.js', { id: target.key, quantity: target.quantity - 1 });
    });
  }

  removeGum() {
    return this.mutate(async () => {
      const updates = {};
      (this.cart ? this.cart.items : []).forEach((i) => {
        if (i.product_id === this.cfg.gumProductId || (i.properties && i.properties._sc_hediye)) updates[i.key] = 0;
      });
      if (Object.keys(updates).length) await cartRequest('/cart/update.js', { updates });
    });
  }

  changeLine(key, quantity) {
    return this.mutate(() => cartRequest('/cart/change.js', { id: key, quantity: Math.max(0, quantity) }));
  }

  addUpsell(variantId) {
    return this.mutate(() => cartRequest('/cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }] }));
  }

  onClick = (event) => {
    const t = event.target;
    if (t === this.dialog || t.closest('[data-sc-cart-close]')) return this.close();
    if (t.closest('[data-sc-gum-plus]')) return this.addBox();
    if (t.closest('[data-sc-gum-minus]')) return this.removeBox();
    if (t.closest('[data-sc-remove-gum]')) return this.removeGum();
    const change = t.closest('[data-sc-change]');
    if (change) return this.changeLine(change.dataset.key, Number(change.dataset.qty));
    const upsell = t.closest('[data-sc-upsell-add]');
    if (upsell) return this.addUpsell(upsell.dataset.scUpsellAdd);
    if (t.closest('[data-sc-edit]')) return this.openSheet();
    if (t.closest('[data-sc-sheet-close]')) return this.closeSheet();
    const sheetAdd = t.closest('[data-sc-sheet-add]');
    if (sheetAdd) return this.sheetAdd(sheetAdd.dataset.scSheetAdd);
    const sheetMinus = t.closest('[data-sc-sheet-minus]');
    if (sheetMinus) return this.sheetMinus(sheetMinus.dataset.scSheetMinus);
    if (t.closest('[data-sc-sheet-save]')) return this.saveSheet();
    return undefined;
  };

  showError(message) {
    const box = this.$('[data-sc-cart-error]');
    if (!box) return;
    box.textContent = message || '';
    box.hidden = !message;
  }

  /* ---------- Çizim ---------- */

  render(cart) {
    if (!cart) return;
    this.cart = cart;
    this.classList.remove('is-loading');
    const s = this.cfg.strings;
    const gum = cart.items.filter((i) => i.product_id === this.cfg.gumProductId);
    const gifts = cart.items.filter((i) => !gum.includes(i) && i.properties && i.properties._sc_hediye);
    const others = cart.items.filter((i) => !gum.includes(i) && !gifts.includes(i));
    const boxes = gum.reduce((n, i) => n + i.quantity, 0);
    const empty = cart.item_count === 0;

    this.$('[data-sc-cart-empty]').hidden = !empty;
    this.$('[data-sc-cart-filled]').hidden = empty;
    this.$('[data-sc-cart-foot]').hidden = empty;
    const count = this.$('[data-sc-cart-count]');
    if (count) count.textContent = cart.item_count ? `(${cart.item_count})` : '';
    if (empty) return;

    const progress = this.$('[data-sc-cart-progress]');
    if (progress) progress.innerHTML = this.progressHtml(boxes);
    this.$('[data-sc-cart-lines]').innerHTML = [
      gum.length ? this.bundleHtml(gum, boxes) : '',
      ...gifts.map((l) => this.giftHtml(l)),
      ...others.map((l) => this.lineHtml(l)),
    ].join('');

    const upsell = this.$('[data-sc-cart-upsell]');
    if (upsell) {
      const upsellId = Number(upsell.dataset.productId);
      upsell.hidden = !upsellId || cart.items.some((i) => i.product_id === upsellId && !(i.properties && i.properties._sc_hediye));
    }

    // Ara toplam: üstü çizili fiyat = karşılaştırma fiyatları toplamı (kampanya oranı varsa toplam / (1 - oran))
    const campaign = Number(this.cfg.campaign) || 0;
    const total = cart.total_price;
    const compare =
      campaign > 0
        ? Math.floor((total * 100) / (100 - campaign))
        : this.cfg.gumCompare * boxes +
          gifts.reduce((n, l) => n + l.original_line_price, 0) +
          others.reduce((n, l) => n + l.original_line_price, 0);
    const off = campaign > 0 ? campaign : compare > total ? 100 - Math.floor((total * 100) / compare) : 0;
    this.$('[data-sc-cart-total]').textContent = this.money(total);
    const was = this.$('[data-sc-cart-was]');
    was.textContent = this.money(compare);
    was.hidden = off <= 0;
    const badge = this.$('[data-sc-cart-badge]');
    badge.textContent = fill(s.offBadge, { indirim: off });
    badge.hidden = off <= 0;
    this.$('[data-sc-checkout-label]').textContent = fill(s.checkout, { fiyat: this.money(total) });
  }

  progressHtml(boxes) {
    const stones = this.cfg.milestones || [];
    if (!stones.length || !this.cfg.gumProductId) return '';
    const s = this.cfg.strings;
    const next = stones.find((m) => boxes < m.at);
    const message = next
      ? esc(fill(s.remaining, { n: next.at - boxes })).replace(esc('[hediye]'), `<strong>${esc(next.label)}</strong>`)
      : esc(s.allUnlocked);

    // Düğümler eşit aralıklı; dolum, iki düğüm arasında orantılı ilerler
    const last = stones.length - 1;
    const pos = (i) => (last === 0 ? 100 : (i / last) * 100);
    let percent = 0;
    if (boxes >= stones[last].at) percent = 100;
    else {
      for (let i = 0; i < stones.length; i += 1) {
        if (boxes < stones[i].at) {
          const prevAt = i === 0 ? 0 : stones[i - 1].at;
          const prevPos = i === 0 ? 0 : pos(i - 1);
          const span = stones[i].at - prevAt;
          percent = prevPos + (span > 0 ? ((boxes - prevAt) / span) * (pos(i) - prevPos) : 0);
          break;
        }
      }
    }

    const nodes = stones
      .map((m, i) => {
        const on = boxes >= m.at;
        return `<li class="sc-ms__node${on ? ' is-on' : ''}" style="--x:${pos(i)}%">
          <span class="sc-ms__dot">${this.icon(m.icon)}</span>
          <span class="sc-ms__label">${esc(m.label)}</span>
        </li>`;
      })
      .join('');

    return `<p class="sc-ms__msg">${message}</p>
      <div class="sc-ms__bar"><span class="sc-ms__fill" style="width:${Math.max(0, Math.min(100, percent))}%"></span>
        <ol class="sc-ms__nodes">${nodes}</ol>
      </div>`;
  }

  bundleHtml(lines, boxes) {
    const s = this.cfg.strings;
    const final = lines.reduce((n, l) => n + l.final_line_price, 0);
    const campaign = Number(this.cfg.campaign) || 0;
    const compare = campaign > 0 ? Math.floor((final * 100) / (100 - campaign)) : this.cfg.gumCompare * boxes;
    const planLine = lines.find((l) => l.selling_plan_allocation);
    const plan = planLine ? planLine.selling_plan_allocation.selling_plan.name : '';
    const freeStone = (this.cfg.milestones || []).find((m) => m.type === 'kutu');
    const freeTag = freeStone && boxes >= freeStone.at ? `<span class="sc-line__tag">${esc(s.freeTag)}</span>` : '';
    const image = this.cfg.gumImage || lines[0].image;
    const chips = lines
      .map(
        (l) => `<span class="sc-chip">${l.image ? `<img src="${esc(sized(l.image, 64))}" alt="" width="20" height="14" loading="lazy">` : ''}${l.quantity}× ${esc(l.variant_title || l.product_title)}</span>`
      )
      .join('');
    const canEdit = this.cfg.flavours.length > 1;

    return `<div class="sc-line sc-line--bundle">
      <div class="sc-line__img"><img src="${esc(sized(image, 200))}" alt="" width="88" height="88" loading="lazy"></div>
      <div class="sc-line__main">
        <div class="sc-line__top">
          <p class="sc-line__title">${esc(this.cfg.gumTitle)}</p>
          <button type="button" class="sc-line__remove" data-sc-remove-gum aria-label="${esc(s.remove)}">${this.icon('trash')}</button>
        </div>
        <p class="sc-line__sub">${esc(fill(s.boxes, { n: boxes }))}${plan ? ` · ${esc(plan)}` : ''}</p>
        <p class="sc-line__price"><b>${this.money(final)}</b>${compare > final ? ` <s>${this.money(compare)}</s>` : ''}</p>
        <div class="sc-line__row">
          <div class="sc-qty">
            <button type="button" data-sc-gum-minus aria-label="${esc(s.minusBox)}">−</button>
            <span>${boxes}</span>
            <button type="button" data-sc-gum-plus aria-label="${esc(s.plusBox)}">+</button>
          </div>
          ${freeTag}
        </div>
        <div class="sc-chips">${chips}${canEdit ? `<button type="button" class="sc-chip sc-chip--edit" data-sc-edit>${this.icon('pen')}${esc(s.edit)}</button>` : ''}</div>
      </div>
    </div>`;
  }

  giftHtml(line) {
    const s = this.cfg.strings;
    return `<div class="sc-line sc-line--gift">
      <div class="sc-line__img">${line.image ? `<img src="${esc(sized(line.image, 160))}" alt="" width="64" height="64" loading="lazy">` : ''}</div>
      <div class="sc-line__main">
        <p class="sc-line__title">${esc(line.product_title)} <span class="sc-line__tag">${esc(s.gift)}</span></p>
        <p class="sc-line__price"><b>${line.final_line_price === 0 ? esc(s.giftPrice) : this.money(line.final_line_price)}</b>${line.original_line_price > line.final_line_price ? ` <s>${this.money(line.original_line_price)}</s>` : ''}</p>
      </div>
    </div>`;
  }

  lineHtml(line) {
    const s = this.cfg.strings;
    const variant = line.variant_title && !/default title/i.test(line.variant_title) ? line.variant_title : '';
    const plan = line.selling_plan_allocation ? line.selling_plan_allocation.selling_plan.name : '';
    return `<div class="sc-line">
      <div class="sc-line__img">${line.image ? `<img src="${esc(sized(line.image, 160))}" alt="" width="64" height="64" loading="lazy">` : ''}</div>
      <div class="sc-line__main">
        <div class="sc-line__top">
          <p class="sc-line__title">${esc(line.product_title)}</p>
          <button type="button" class="sc-line__remove" data-sc-change data-key="${esc(line.key)}" data-qty="0" aria-label="${esc(s.remove)}">${this.icon('trash')}</button>
        </div>
        ${variant || plan ? `<p class="sc-line__sub">${esc([variant, plan].filter(Boolean).join(' · '))}</p>` : ''}
        <p class="sc-line__price"><b>${this.money(line.final_line_price)}</b>${line.original_line_price > line.final_line_price ? ` <s>${this.money(line.original_line_price)}</s>` : ''}</p>
        <div class="sc-line__row">
          <div class="sc-qty">
            <button type="button" data-sc-change data-key="${esc(line.key)}" data-qty="${line.quantity - 1}" aria-label="${esc(s.minus)}">−</button>
            <span>${line.quantity}</span>
            <button type="button" data-sc-change data-key="${esc(line.key)}" data-qty="${line.quantity + 1}" aria-label="${esc(s.plus)}">+</button>
          </div>
        </div>
      </div>
    </div>`;
  }

  /* ---------- Tat düzenleme (alttan açılan panel) ---------- */

  openSheet() {
    const lines = this.gumLines();
    this.sheetTotal = lines.reduce((n, l) => n + l.quantity, 0);
    this.sheetCounts = {};
    this.cfg.flavours.forEach((f) => (this.sheetCounts[f.id] = 0));
    lines.forEach((l) => (this.sheetCounts[String(l.variant_id)] = (this.sheetCounts[String(l.variant_id)] || 0) + l.quantity));
    this.paintSheet();
    const sheet = this.$('[data-sc-sheet]');
    sheet.hidden = false;
    requestAnimationFrame(() => sheet.classList.add('is-open'));
  }

  closeSheet() {
    const sheet = this.$('[data-sc-sheet]');
    if (!sheet || sheet.hidden) return;
    sheet.classList.remove('is-open');
    setTimeout(() => (sheet.hidden = true), reduceMotion ? 0 : 250);
  }

  sheetChosen() {
    return Object.values(this.sheetCounts).reduce((n, q) => n + q, 0);
  }

  sheetAdd(id) {
    const flavour = this.cfg.flavours.find((f) => f.id === id);
    if (!flavour || !flavour.available) return;
    if (this.sheetChosen() < this.sheetTotal) {
      this.sheetCounts[id] += 1;
    } else {
      const donor = this.cfg.flavours
        .filter((f) => f.id !== id && this.sheetCounts[f.id] > 0)
        .sort((a, b) => this.sheetCounts[b.id] - this.sheetCounts[a.id])[0];
      if (!donor) return;
      this.sheetCounts[donor.id] -= 1;
      this.sheetCounts[id] += 1;
    }
    this.paintSheet();
  }

  sheetMinus(id) {
    if (!this.sheetCounts[id]) return;
    this.sheetCounts[id] -= 1;
    this.paintSheet();
  }

  paintSheet() {
    const s = this.cfg.strings;
    const left = this.sheetTotal - this.sheetChosen();
    const fallback = this.cfg.flavours.find((f) => f.id === String(this.cfg.defaultVariant)) || this.cfg.flavours[0];
    this.$('[data-sc-sheet-note]').textContent =
      left > 0 ? fill(s.sheetNote, { n: left, tat: fallback ? fallback.title : '' }) : s.sheetDone;
    this.$('[data-sc-sheet-grid]').innerHTML = this.cfg.flavours
      .map((f) => {
        const q = this.sheetCounts[f.id] || 0;
        return `<div class="sc-tile${q > 0 ? ' is-on' : ''}${f.available ? '' : ' is-soldout'}">
          <button type="button" class="sc-tile__pick" data-sc-sheet-add="${esc(f.id)}"${f.available ? '' : ' disabled'} aria-label="${esc(f.title)} +1">
            <span class="sc-tile__img">${f.image ? `<img src="${esc(f.image)}" alt="" width="120" height="120" loading="lazy">` : ''}</span>
            <span class="sc-tile__name">${esc(f.title)}</span>
          </button>
          <span class="sc-step">
            <button type="button" class="sc-step__btn" data-sc-sheet-minus="${esc(f.id)}"${q === 0 ? ' disabled' : ''} aria-label="${esc(f.title)} −1">−</button>
            <span class="sc-step__qty">${q}</span>
            <button type="button" class="sc-step__btn is-plus" data-sc-sheet-add="${esc(f.id)}"${f.available ? '' : ' disabled'} aria-label="${esc(f.title)} +1">+</button>
          </span>
        </div>`;
      })
      .join('');
  }

  saveSheet() {
    const lines = this.gumLines();
    const counts = { ...this.sheetCounts };
    const left = this.sheetTotal - this.sheetChosen();
    const fallbackId = String(this.cfg.defaultVariant || (this.cfg.flavours[0] && this.cfg.flavours[0].id));
    if (left > 0) counts[fallbackId] = (counts[fallbackId] || 0) + left;
    const planLine = lines.find((l) => l.selling_plan_allocation);
    const plan = planLine ? { selling_plan: planLine.selling_plan_allocation.selling_plan.id } : {};
    const properties = (lines[0] && lines[0].properties) || {};
    this.closeSheet();
    return this.mutate(async () => {
      const updates = {};
      lines.forEach((l) => (updates[l.key] = 0));
      await cartRequest('/cart/update.js', { updates });
      const items = Object.entries(counts)
        .filter(([, q]) => q > 0)
        .map(([id, q]) => ({ id: Number(id), quantity: q, properties, ...plan }));
      if (items.length) await cartRequest('/cart/add.js', { items });
    });
  }
}

if (!customElements.get('sc-cart')) customElements.define('sc-cart', ScCart);
